import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const RESOLVED_STATUSES = new Set([
  'acknowledged',
  'discarded',
  'reconciled',
  'requeued'
]);

function normalizedStatus(data) {
  return [data?.status, data?.recoveryStatus]
    .map(value => String(value || '').trim().toLowerCase())
    .filter(Boolean);
}

function isResolved(data) {
  return normalizedStatus(data).some(status => RESOLVED_STATUSES.has(status));
}

function isUnsentReplyReview(data) {
  if (data?.alreadySent || String(data?.source || '').trim() !== 'pendingResponses') return false;
  const reason = `${data?.failureReason || ''} ${data?.lastError || ''}`.toLowerCase();
  return reason.includes('manual review') || reason.includes('automatic inbox replies are disabled');
}

function normalizeItem(docSnap) {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    clientId: data.clientId || null,
    threadId: data.threadId || null,
    replyToMessageId: data.msgId || data.replyToMessageId || null,
    recipient: data.recipient || data.email || null,
    draftBody: data.responseBody || data.script || data.body || '',
    subject: data.subject || '',
    reason: data.failureReason || data.lastError || 'This message could not be completed.',
    createdAt: data.createdAt || data.deadLetteredAt || data.movedAt || null,
    raw: data
  };
}

export function useDeadLetterReviews(uid) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(Boolean(uid));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!uid) {
      setItems([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    return onSnapshot(
      collection(db, 'users', uid, 'deadLetterQueue'),
      snapshot => {
        setItems(snapshot.docs
          .filter(docSnap => !isResolved(docSnap.data() || {}))
          .map(docSnap => ({ ...normalizeItem(docSnap), reviewRequired: isUnsentReplyReview(docSnap.data() || {}) }))
        );
        setLoading(false);
        setError(null);
      },
      snapshotError => {
        setItems([]);
        setLoading(false);
        setError(snapshotError);
      }
    );
  }, [uid]);

  return useMemo(() => ({
    reviewItems: items.filter(item => item.reviewRequired),
    failureItems: items.filter(item => !item.reviewRequired),
    loading,
    error
  }), [items, loading, error]);
}
